import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  useAdminWorlds,
  useCreateWorld,
  useDeleteWorld,
  useDiscoverWorlds,
  useSyncWorld,
  useUpdateWorld,
} from '../api/queries';
import {
  WORLD_STATUS_LABELS,
  type DiscoveredWorld,
  type World,
  type WorldStatus,
} from '../api/types';
import { Spinner } from '../components/Spinner';
import { useToast } from '../components/Toast';

const STATUS_OPTIONS: WorldStatus[] = ['open', 'closed', 'archived'];

function formatDateTime(value: string | null): string {
  return value ? new Date(value).toLocaleString('de-DE') : '—';
}

export function AdminWorldsPage() {
  const worlds = useAdminWorlds();
  const rows = worlds.data?.worlds ?? [];
  const toast = useToast();

  return (
    <>
      <CreateWorldForm />

      <section className="card">
        <div className="card-head">
          <div>
            <h2>Welten</h2>
            <p className="muted">
              Jede Welt ist ein eigener Mandant. Konten gehören zu genau einer Welt; du als
              Administrator siehst alle.
            </p>
          </div>
          <button
            type="button"
            className="btn"
            onClick={() => worlds.refetch()}
            disabled={worlds.isFetching}
          >
            {worlds.isFetching ? 'Lädt …' : 'Aktualisieren'}
          </button>
        </div>

        {worlds.isPending && <Spinner label="Welten werden geladen …" />}

        {worlds.isError && (
          <div className="flash flash-error">
            {worlds.error instanceof ApiError
              ? worlds.error.message
              : 'Welten konnten nicht geladen werden.'}
          </div>
        )}

        {!worlds.isPending && rows.length === 0 && (
          <p className="muted center pad">
            Noch keine Welt angelegt. Ohne Welt kann sich niemand registrieren.
          </p>
        )}

        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Kürzel</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th className="right">Konten</th>
                  <th className="right">Offen</th>
                  <th>Weltdaten</th>
                  <th className="right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((world) => (
                  <WorldRow key={world.id} world={world} onMessage={toast.push} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function CreateWorldForm() {
  const createWorld = useCreateWorld();
  const discover = useDiscoverWorlds();
  const toast = useToast();
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [syncHost, setSyncHost] = useState('');
  const [platformHost, setPlatformHost] = useState('');
  const [discovered, setDiscovered] = useState<DiscoveredWorld[] | null>(null);

  const error = createWorld.error instanceof ApiError ? createWorld.error : null;
  const fieldError = (field: string): string | undefined =>
    error?.fields[field] ?? (error?.field === field ? error.message : undefined);
  const generalError = error && !error.field ? error.message : undefined;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    createWorld.mutate(
      { key, name, ...(syncHost ? { syncHost } : {}) },
      {
        onSuccess: (data) => {
          toast.push('success', `Welt "${data.world.name}" wurde angelegt.`);
          setKey('');
          setName('');
          setSyncHost('');
        },
      },
    );
  };

  const handleDiscover = (event: FormEvent) => {
    event.preventDefault();
    discover.mutate(
      { host: platformHost },
      {
        onSuccess: (data) => setDiscovered(data.worlds),
        onError: (discoverError) => toast.push('error', discoverError.message),
      },
    );
  };

  const pick = (world: DiscoveredWorld) => {
    setKey(world.key);
    setSyncHost(world.host);
    setName((current) => current || world.key);
  };

  return (
    <section className="card">
      <h1>Welt anlegen</h1>
      <p className="muted">
        Das Kürzel identifiziert die Welt in Formularen und Links — angelehnt an die Schreibweise
        im Spiel, etwa <code>de233</code>.
      </p>

      <details className="howto">
        <summary>Welten von der Plattform übernehmen</summary>
        <p className="muted">
          Gib die Plattform an (z. B. <code>die-staemme.de</code> oder <code>staemme.ch</code>),
          um ihre aktuell laufenden Welten samt Adresse zu sehen.
        </p>
        <form className="inline-fields" onSubmit={handleDiscover} noValidate>
          <label className="field grow">
            <span>Plattform</span>
            <input
              type="text"
              value={platformHost}
              onChange={(event) => setPlatformHost(event.target.value)}
              placeholder="die-staemme.de"
            />
          </label>
          <button type="submit" className="btn" disabled={discover.isPending || !platformHost}>
            {discover.isPending ? 'Wird geladen …' : 'Welten anzeigen'}
          </button>
        </form>

        {discovered && discovered.length > 0 && (
          <div className="chip-row">
            {discovered.map((world) => (
              <button
                key={world.key}
                type="button"
                className="chip-button"
                onClick={() => pick(world)}
                title={world.host}
              >
                {world.key}
              </button>
            ))}
          </div>
        )}
        {discovered && discovered.length === 0 && (
          <p className="muted">Diese Plattform meldet zurzeit keine laufenden Welten.</p>
        )}
      </details>

      {generalError && <div className="flash flash-error">{generalError}</div>}

      <form className="inline-fields" onSubmit={handleSubmit} noValidate>
        <label className="field">
          <span>Kürzel</span>
          <input
            type="text"
            value={key}
            onChange={(event) => setKey(event.target.value)}
            placeholder="de233"
            required
          />
          {fieldError('key') && <small className="field-error">{fieldError('key')}</small>}
        </label>

        <label className="field grow">
          <span>Name</span>
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Welt 233"
            required
          />
          {fieldError('name') && <small className="field-error">{fieldError('name')}</small>}
        </label>

        <label className="field grow">
          <span>Sync-Host (optional)</span>
          <input
            type="text"
            value={syncHost}
            onChange={(event) => setSyncHost(event.target.value)}
            placeholder="https://de233.die-staemme.de"
          />
          {fieldError('syncHost') && <small className="field-error">{fieldError('syncHost')}</small>}
        </label>

        <button type="submit" className="btn btn-primary" disabled={createWorld.isPending}>
          {createWorld.isPending ? 'Wird angelegt …' : 'Anlegen'}
        </button>
      </form>
      <p className="hint">
        Mit Sync-Host lädt das Tool Dörfer, Spieler und Stämme dieser Welt stündlich automatisch
        nach. Lässt sich auch später über „Mehr" bei der Welt nachtragen.
      </p>
    </section>
  );
}

function WorldRow({
  world,
  onMessage,
}: {
  world: World;
  onMessage: (kind: 'success' | 'error' | 'info', message: string) => void;
}) {
  const update = useUpdateWorld();
  const remove = useDeleteWorld();
  const sync = useSyncWorld();
  const [name, setName] = useState(world.name);
  const [syncHost, setSyncHost] = useState(world.syncHost ?? '');
  const [substantialBp, setSubstantialBp] = useState(world.villageIntelSubstantialBp?.toString() ?? '');
  const [defaultDays, setDefaultDays] = useState(world.villageIntelDefaultDays?.toString() ?? '');
  const [maxAxe, setMaxAxe] = useState(world.villageIntelMaxAxe?.toString() ?? '');
  const [maxLight, setMaxLight] = useState(world.villageIntelMaxLight?.toString() ?? '');
  const [maxRam, setMaxRam] = useState(world.villageIntelMaxRam?.toString() ?? '');
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const busy = update.isPending || remove.isPending || sync.isPending;
  const fail = (error: Error) => onMessage('error', error.message);

  const changeStatus = (status: WorldStatus) => {
    if (status === 'archived' && !window.confirm(
      `"${world.name}" archivieren? Mitglieder dieser Welt werden sofort abgemeldet und kommen nicht mehr hinein.`,
    )) {
      return;
    }

    update.mutate(
      { id: world.id, status },
      {
        onSuccess: () =>
          onMessage('success', `"${world.name}": ${WORLD_STATUS_LABELS[status]}.`),
        onError: fail,
      },
    );
  };

  const saveName = () => {
    update.mutate(
      { id: world.id, name },
      {
        onSuccess: () => {
          onMessage('success', 'Name wurde geändert.');
          setEditing(false);
        },
        onError: fail,
      },
    );
  };

  const saveSyncHost = () => {
    update.mutate(
      { id: world.id, syncHost: syncHost.trim() || null },
      {
        onSuccess: () => onMessage('success', 'Sync-Host wurde gespeichert.'),
        onError: fail,
      },
    );
  };

  /** Leeres Feld -- die im Code eingebaute Vorgabe verwenden (`null`, löscht eine gesetzte Welteinstellung wieder). */
  const saveVillageIntelSettings = () => {
    update.mutate(
      {
        id: world.id,
        villageIntelSubstantialBp: substantialBp.trim() ? Number(substantialBp) : null,
        villageIntelDefaultDays: defaultDays.trim() ? Number(defaultDays) : null,
      },
      {
        onSuccess: () => onMessage('success', 'Off-/Deff-Einstellungen wurden gespeichert.'),
        onError: fail,
      },
    );
  };

  /** Leeres Feld -- die im Code eingebaute Vorgabe verwenden (`null`, löscht eine gesetzte Obergrenze wieder). */
  const saveVillageIntelUnitCaps = () => {
    update.mutate(
      {
        id: world.id,
        villageIntelMaxAxe: maxAxe.trim() ? Number(maxAxe) : null,
        villageIntelMaxLight: maxLight.trim() ? Number(maxLight) : null,
        villageIntelMaxRam: maxRam.trim() ? Number(maxRam) : null,
      },
      {
        onSuccess: () => onMessage('success', 'Obergrenzen der Truppenhochrechnung wurden gespeichert.'),
        onError: fail,
      },
    );
  };

  const triggerSync = () => {
    sync.mutate(
      { id: world.id },
      {
        onSuccess: () => onMessage('success', `Weltdaten von "${world.name}" wurden aktualisiert.`),
        onError: fail,
      },
    );
  };

  const confirmDelete = () => {
    if (!window.confirm(`Welt "${world.name}" löschen?`)) return;
    remove.mutate(
      { id: world.id },
      {
        onSuccess: () => onMessage('success', `Welt "${world.name}" wurde gelöscht.`),
        onError: fail,
      },
    );
  };

  return (
    <>
      <tr className={world.status === 'archived' ? 'row-muted' : undefined}>
        <td>
          <code>{world.key}</code>
        </td>
        <td>
          {editing ? (
            <div className="detail-row">
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoFocus
              />
              <button type="button" className="btn btn-sm" disabled={busy} onClick={saveName}>
                Speichern
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setName(world.name);
                  setEditing(false);
                }}
              >
                Abbrechen
              </button>
            </div>
          ) : (
            <button type="button" className="link-button" onClick={() => setEditing(true)}>
              {world.name}
            </button>
          )}
        </td>
        <td>
          <select
            className="status-select"
            value={world.status}
            disabled={busy}
            onChange={(event) => changeStatus(event.target.value as WorldStatus)}
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {WORLD_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </td>
        <td className="right">
          <Link to={`/admin/benutzer?welt=${world.key}`}>{world.counts.total}</Link>
        </td>
        <td className="right">
          {world.counts.pending > 0 ? (
            <Link className="stat-warn" to={`/admin/benutzer?welt=${world.key}&status=pending`}>
              {world.counts.pending}
            </Link>
          ) : (
            <span className="muted">0</span>
          )}
        </td>
        <td>
          {world.syncHost ? (
            world.syncError ? (
              <span className="sync-status sync-error" title={world.syncError}>
                Fehler
              </span>
            ) : world.syncedAt ? (
              <span className="sync-status sync-ok">
                {world.gameData.villages.toLocaleString('de-DE')} Dörfer
              </span>
            ) : (
              <span className="sync-status muted">noch nicht synchronisiert</span>
            )
          ) : (
            <span className="muted">kein Sync-Host</span>
          )}
        </td>
        <td className="right">
          <div className="actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? 'Weniger ▲' : 'Mehr ▾'}
            </button>
          </div>
        </td>
      </tr>

      {menuOpen && (
        <tr className="row-detail">
          <td colSpan={7}>
            <div className="detail-panel">
              <div className="detail-block">
                <span className="detail-title">Sync-Host</span>
                <div className="detail-row">
                  <input
                    type="text"
                    value={syncHost}
                    placeholder="https://de233.die-staemme.de"
                    onChange={(event) => setSyncHost(event.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy || syncHost.trim() === (world.syncHost ?? '')}
                    onClick={saveSyncHost}
                  >
                    Speichern
                  </button>
                </div>
                <small className="hint">
                  Basis-Adresse der Welt auf der Spielplattform. Grundlage für den stündlichen
                  Abgleich von Dörfern, Spielern und Stämmen.
                </small>
              </div>

              <div className="detail-block">
                <span className="detail-title">Off-/Deff-Erkennung</span>
                <div className="detail-row">
                  <label className="field" style={{ maxWidth: '180px' }}>
                    <span>Substanziell-Schwelle (BP)</span>
                    <input
                      type="number"
                      min={0}
                      value={substantialBp}
                      placeholder="1000 (Vorgabe)"
                      onChange={(event) => setSubstantialBp(event.target.value)}
                    />
                  </label>
                  <label className="field" style={{ maxWidth: '160px' }}>
                    <span>Standard-Zeitraum (Tage)</span>
                    <input
                      type="number"
                      min={1}
                      max={90}
                      value={defaultDays}
                      placeholder="14 (Vorgabe)"
                      onChange={(event) => setDefaultDays(event.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={
                      busy ||
                      (substantialBp.trim() === (world.villageIntelSubstantialBp?.toString() ?? '') &&
                        defaultDays.trim() === (world.villageIntelDefaultDays?.toString() ?? ''))
                    }
                    onClick={saveVillageIntelSettings}
                  >
                    Speichern
                  </button>
                </div>
                <small className="hint">
                  Steuern die Off-/Deff-Dorf-Erkennung (/dorftypen) dieser Welt: die
                  Bauernhofplätze-Schwelle, ab der eine Truppenmenge als "substanziell" statt als
                  Fake zählt, und der beim Öffnen der Seite vorausgewählte Zeitraum. Leer lassen
                  verwendet die eingebaute Vorgabe (1.000 BP bzw. 14 Tage).
                </small>
              </div>

              <div className="detail-block">
                <span className="detail-title">Truppenhochrechnung: Obergrenzen</span>
                <div className="detail-row">
                  <label className="field" style={{ maxWidth: '140px' }}>
                    <span>Max. Axt</span>
                    <input
                      type="number"
                      min={0}
                      value={maxAxe}
                      placeholder="6000 (Vorgabe)"
                      onChange={(event) => setMaxAxe(event.target.value)}
                    />
                  </label>
                  <label className="field" style={{ maxWidth: '140px' }}>
                    <span>Max. Leichte Kavallerie</span>
                    <input
                      type="number"
                      min={0}
                      value={maxLight}
                      placeholder="2500 (Vorgabe)"
                      onChange={(event) => setMaxLight(event.target.value)}
                    />
                  </label>
                  <label className="field" style={{ maxWidth: '140px' }}>
                    <span>Max. Ramme</span>
                    <input
                      type="number"
                      min={0}
                      value={maxRam}
                      placeholder="300 (Vorgabe)"
                      onChange={(event) => setMaxRam(event.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={
                      busy ||
                      (maxAxe.trim() === (world.villageIntelMaxAxe?.toString() ?? '') &&
                        maxLight.trim() === (world.villageIntelMaxLight?.toString() ?? '') &&
                        maxRam.trim() === (world.villageIntelMaxRam?.toString() ?? ''))
                    }
                    onClick={saveVillageIntelUnitCaps}
                  >
                    Speichern
                  </button>
                </div>
                <small className="hint">
                  Deckeln die Off-Dorf-Truppenhochrechnung ("bekanntes Offdorf, mögliche
                  Truppen: ~X") je Einheitentyp -- ohne Deckel könnte ein alter Ausgangsbeleg
                  unrealistische Werte liefern, die am Bauernhofplätze-Cap eines Dorfs vorbeirechnen.
                  Leer lassen verwendet die eingebaute Vorgabe (6.000 Axt, 2.500 Leichte
                  Kavallerie, 300 Rammen).
                </small>
              </div>

              <div className="detail-block">
                <span className="detail-title">Weltdaten</span>
                <dl className="detail-facts">
                  <div>
                    <dt>Dörfer / Spieler / Stämme</dt>
                    <dd>
                      {world.gameData.villages.toLocaleString('de-DE')} /{' '}
                      {world.gameData.players.toLocaleString('de-DE')} /{' '}
                      {world.gameData.tribes.toLocaleString('de-DE')}
                    </dd>
                  </div>
                  <div>
                    <dt>Zuletzt synchronisiert</dt>
                    <dd>{formatDateTime(world.syncedAt)}</dd>
                  </div>
                  {world.syncError && (
                    <div>
                      <dt>Letzter Fehler</dt>
                      <dd className="sync-error">{world.syncError}</dd>
                    </div>
                  )}
                </dl>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={busy || !world.syncHost}
                  onClick={triggerSync}
                  title={world.syncHost ? undefined : 'Erst einen Sync-Host hinterlegen.'}
                >
                  {sync.isPending ? 'Synchronisiert …' : 'Jetzt synchronisieren'}
                </button>
              </div>

              <div className="detail-block">
                <span className="detail-title">Welt entfernen</span>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  disabled={busy || world.counts.total > 0}
                  title={
                    world.counts.total > 0
                      ? 'Welten mit Konten lassen sich nicht löschen.'
                      : 'Welt löschen'
                  }
                  onClick={confirmDelete}
                >
                  Löschen
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
